import { ConfigurableFocusTrapFactory, FocusTrap } from '@angular/cdk/a11y';
import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  ElementRef,
  HostBinding,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { DomService } from '@rd/core/services/dom/dom.service';
import { ErrorMessageHelper } from '@rd/helpers/error-message.helper';
import { Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DialogConfig } from '../../dialog-config';
import { DialogRef } from '../../dialog-ref';

@Component({
  selector: 'rd-popup-dialog',
  templateUrl: './dialog.component.html',
  styleUrls: ['./dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default, // FIXME: Remove this after upgrading to Angular 14
})
export class DialogComponent implements AfterViewInit, OnDestroy, OnInit {
  componentRef: ComponentRef<any>;
  ariaLabel: string;
  screenModeClass: string;

  @ViewChild('viewChild', { read: ViewContainerRef })
  viewChild: ViewContainerRef;
  @HostBinding('class.rd-popup-dialog') componentClass = true;
  @HostBinding('attr.popup-override') overrideAttribute = true;

  private routerSubscription$ = Subscription.EMPTY;
  private onCloseSubject = new Subject<void>();
  onClose = this.onCloseSubject.asObservable();

  childComponentType: Type<any>;

  // Element that was previously focused before this dialog is opened
  previouslyFocusedElement: HTMLElement | null = null;

  focusTrap: FocusTrap;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private componentFactoryResolver: ComponentFactoryResolver,
    private cdRef: ChangeDetectorRef,
    private domService: DomService,
    private elementRef: ElementRef,
    private errorMessageHelper: ErrorMessageHelper,
    private dialogRef: DialogRef,
    private focusTrapFactory: ConfigurableFocusTrapFactory,
    private dialogConfig: DialogConfig,
    private router: Router,
  ) {
    this.document = document as Document;
  }

  ngOnInit(): void {
    this.ariaLabel =
      this.dialogConfig.ariaLabel || this.getErrorDialogAriaLabel();
    this.screenModeClass = `${this.dialogConfig.screenMode}-dialog`;
    this.routerSubscription$ = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(
        // @ts-ignore
        (e: NavigationEnd) => {
          // default we want to close modal on when route change
          // e.g. location modal on offer details should be closed
          // when user click on back button to go back offer listing page
          // but auth guard modal should not be closed
          if (!this.dialogConfig.keepModalOnRouteChange) {
            this.dialogRef.close();
          }
        },
      );
  }

  get shouldBeRounded(): boolean {
    return this.dialogConfig.screenMode !== 'fullscreen';
  }

  ngAfterViewInit(): void {
    this.domService.lockBody();
    if (this.childComponentType) {
      this.loadChildComponent(this.childComponentType);
    } else {
      this.dialogRef.close();
    }
    this.cdRef.detectChanges();

    if (this.dialogConfig.data?.openEventType === 'mouse') {
      return;
    }

    this.savePreviouslyFocusedElement();
    this.trapFocus();
  }

  ngOnDestroy(): void {
    if (this.componentRef) {
      this.componentRef.destroy();
    }
    this.domService.unlockBody();
    this.restoreFocus();
    this.routerSubscription$.unsubscribe();
  }

  onOverlayClicked(e: MouseEvent): void {
    if (!this.dialogConfig.keepModalOnOverlayClick) {
      this.dialogRef.close({ isOverlayClick: true });
    }
  }

  onDialogClicked(e: MouseEvent): void {
    e.stopPropagation();
  }

  @HostListener('body:keydown.esc')
  onEscape(): void {
    if (!this.dialogConfig.keepModalOnEscapeKey) {
      this.dialogRef.close();
    }
  }

  loadChildComponent(componentType: Type<any>): void {
    const componentFactory =
      this.componentFactoryResolver.resolveComponentFactory(componentType);
    this.viewChild.clear();
    this.componentRef = this.viewChild.createComponent(componentFactory);
  }

  close(): void {
    this.onCloseSubject.next();
  }

  dialogId(): string {
    return this.dialogConfig.id;
  }

  private getErrorDialogAriaLabel(): string {
    if (this.dialogConfig.data?.error) {
      return this.errorMessageHelper.getErrorTitle(
        this.dialogConfig.data.error,
      );
    }
    return '';
  }

  private trapFocus(): void {
    this.focusTrap = this.focusTrapFactory.create(
      this.elementRef.nativeElement,
    );
    this.focusTrap.focusFirstTabbableElementWhenReady();
  }

  private savePreviouslyFocusedElement(): void {
    // In case we ever render this app from the server, we don't wanna bother doing this since Document won't exist
    if (document) {
      this.previouslyFocusedElement = this.document
        .activeElement as HTMLElement;
    }
  }

  private restoreFocus(): void {
    if (this.dialogConfig.data?.openEventType === 'mouse') {
      return;
    }

    const toFocus = this.previouslyFocusedElement;

    // We need the extra check, because IE can set the `activeElement` to null in some cases.
    if (toFocus && typeof toFocus.focus === 'function') {
      const activeElement = this.document.activeElement;
      const element = this.elementRef.nativeElement;

      // Refocus back to previously saved element if:
      // 1. activeElement is gone
      // 2. user clicked on a non-focusable element, in which focus returns to body
      // 3. user was focused on an element inside the dialog (possibly a close button)
      if (
        !activeElement ||
        activeElement === this.document.body ||
        activeElement === element ||
        element.contains(activeElement)
      ) {
        toFocus.focus();
      }

      this.previouslyFocusedElement = null;
    }

    // Only try to destroy focusTrap if it has been created
    if (this.focusTrap) {
      this.focusTrap.destroy();
    }
  }
}
